import {
  forwardRef,
  ReactElement,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ChevronDownIcon } from "@twilio-paste/icons/esm/ChevronDownIcon";
import { HelpText } from "@twilio-paste/core/help-text";
import { Input } from "@twilio-paste/core/input";
import { Label } from "@twilio-paste/core/label";
import {
  Menu,
  MenuButton,
  MenuInitialStateProps,
  MenuItem,
  useMenuState,
} from "@twilio-paste/core/menu";
import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";

import { codes, CountryCodeItemType } from "./definitions/countries";
import { types } from "./definitions";
import FlagIcon from "./sub/FlagIcon";

const defaultCountryBrowser = navigator.language.split("-")[1] || "US";

const CompositeInputPhoneNumber = forwardRef(
  (
    {
      id,
      error,
      label,
      placeholder,
      countryRef,
      defaultCountry = defaultCountryBrowser,
      placement,
      onEnter,
      onChange,
    }: types.CompositeInputPhoneNumberProps,
    ref: Ref<HTMLInputElement>
  ): ReactElement => {
    const menuStateProps: MenuInitialStateProps = {
      placement: placement ?? "bottom-start",
    };
    if (
      menuStateProps.placement === "top-start" ||
      menuStateProps.placement === "bottom-start"
    ) {
      menuStateProps.unstable_offset = [-10, 12]; // Manual offset due to the reset variant of the MenuButton
    }

    const menu = useMenuState(menuStateProps);
    const innerRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

    const safeDefaultCountry = defaultCountry
      ? defaultCountry.toUpperCase()
      : defaultCountryBrowser.toUpperCase();
    const initalCountry =
      codes.find((country) => country.isoCode2 === safeDefaultCountry) ||
      codes[0];

    const [selectedCountry, setSelectedCountry] =
      useState<CountryCodeItemType>(initalCountry);

    useEffect(() => {
      if (countryRef?.current) {
        countryRef.current.value = selectedCountry.countryCodes[0];
      }
    }, []);

    const handleKeyChange = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        if (innerRef.current?.value) {
          onEnter && onEnter();
        }
      }
    };

    const handleFlagIconClick = (country: CountryCodeItemType) => {
      setSelectedCountry(country);
      menu.hide();
      innerRef.current?.focus();

      if (countryRef && countryRef.current) {
        countryRef.current.value = country.countryCodes[0];
      }
    };

    const renderCountriesMenu = () => {
      return (
        <>
          <MenuButton {...menu} variant="reset">
            <FlagIcon country={selectedCountry.isoCode2} />
            {`+${selectedCountry.countryCodes[0]}`}
            <ChevronDownIcon decorative />
          </MenuButton>
          <Menu
            ref={countryRef}
            {...menu}
            aria-label="Select Country Code"
            element="MENU_COUNTRIES_PHONE"
          >
            {codes.map((country) => (
              <MenuItem
                key={country.isoCode2}
                {...menu}
                onClick={() => handleFlagIconClick(country)}
              >
                <Box display={"flex"} alignItems={"center"}>
                  <FlagIcon country={country.isoCode2} />
                  <Text as="span">{`${country.country} +${country.countryCodes[0]}`}</Text>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      );
    };

    return (
      <>
        {label && (
          <Label htmlFor={id} required>
            {label}
          </Label>
        )}
        <Input
          id={id}
          name="phone_number"
          placeholder={placeholder}
          type="tel"
          required
          ref={innerRef}
          onKeyUp={handleKeyChange}
          onChange={onChange}
          autoFocus
          insertBefore={renderCountriesMenu()}
        />
        {error && <HelpText variant="error">{error}</HelpText>}
      </>
    );
  }
);

export default CompositeInputPhoneNumber;
